import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { IconKey } from "@core/assets";
import {
  Badge,
  ErrorPanel,
  PageHeader,
  SkeletonRows,
  Tabs,
} from "@core/components";
import { useAppel } from "../../detail";
import { STEP_KEYS, stepState, type StepKey } from "../../_shared/types";

/** The two steps that have a screen so far; the three after them stay locked. */
const STEP_ROUTES: Partial<Record<StepKey, "programme" | "eligibility">> = {
  PROGRAMME: "programme",
  ELIGIBILITY: "eligibility",
};

const STATE_ICON: Record<"COMPLETED" | "IN_PROGRESS" | "LOCKED", IconKey> = {
  COMPLETED: "circle-check",
  IN_PROGRESS: "circle-dashed",
  LOCKED: "lock",
};

/**
 * The appel level of the frame (Figma "Appel config"): the back arrow to the cohort page, the
 * appel's name with its status, the five step tabs, and the step below. Each tab carries the state
 * the server reports: ticked when its step is saved, a dashed circle while it is open, a padlock
 * (and not selectable) while it is locked. The step on screen follows the route, so a reload or a
 * shared link lands on the same step.
 *
 * Every step saves on its own, so leaving and coming back finds what was saved.
 */
export function AppelFrame({
  cohortId,
  appelId,
}: {
  cohortId: string;
  appelId: string;
}) {
  const { t } = useTranslation("appels");
  const navigate = useNavigate();
  const location = useLocation();
  const appel = useAppel(appelId);

  if (appel.isPending) return <SkeletonRows rows={3} />;
  if (appel.isError) {
    return (
      <ErrorPanel
        isRetrying={appel.isFetching}
        onRetry={() => {
          void appel.refetch();
        }}
      />
    );
  }

  const data = appel.data;
  const current = location.pathname.endsWith("/eligibility")
    ? "ELIGIBILITY"
    : "PROGRAMME";

  return (
    <>
      <PageHeader
        title={data.name}
        onBack={() => {
          void navigate({ to: "/cohorts/$cohortId", params: { cohortId } });
        }}
        chips={
          <Badge>
            {data.status === "DRAFT" ? t("status.draft") : data.status}
          </Badge>
        }
      />

      <Tabs
        aria-label={t("steps.label")}
        value={current}
        onChange={(id) => {
          const route = STEP_ROUTES[id as StepKey];
          if (route === "programme") {
            void navigate({
              to: "/cohorts/$cohortId/appels/$appelId/programme",
              params: { cohortId, appelId },
            });
          } else if (route === "eligibility") {
            void navigate({
              to: "/cohorts/$cohortId/appels/$appelId/eligibility",
              params: { cohortId, appelId },
            });
          }
        }}
        items={STEP_KEYS.map((key) => {
          const state = stepState(data, key);
          return {
            id: key,
            label: t(`steps.${key}`),
            icon: STATE_ICON[state],
            isDisabled: state === "LOCKED" || STEP_ROUTES[key] === undefined,
          };
        })}
      />

      <Outlet />
    </>
  );
}
